import type { HTMLAttributes, ReactNode } from 'react';

export type CardVariant = 'plain' | 'interactive' | 'danger' | 'quiet';

/**
 * A hundred and fourteen `className="card"` strings, most of them carrying the
 * same three or four extra utilities. The variants below are the shapes that
 * actually recur: a row you can tap (money rows, payroll history), a warning
 * card, and the sand-tinted quiet card used for read-only detail.
 *
 * `cardClass` exists for the same reason `buttonClass` does — a card is often
 * a <Link> or a <button>, and those cannot be this component.
 */
export function cardClass(variant: CardVariant = 'plain', className = ''): string {
  const variants: Record<CardVariant, string> = {
    plain: 'card',
    interactive: 'card w-full text-start transition-all active:scale-[0.99]',
    danger: 'card border-urgent/25 bg-urgent/10 text-urgent',
    quiet: 'card bg-muted border-transparent shadow-none',
  };
  const base = variants[variant];
  return className ? `${base} ${className}` : base;
}

export function Card({
  variant = 'plain',
  className = '',
  children,
  ...rest
}: {
  variant?: CardVariant;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={cardClass(variant, className)}>
      {children}
    </div>
  );
}
